/**
 * Delivery Estimate Calculator
 * Calculates and displays the estimated delivery date on the PDP.
 *
 * Reads configuration from data attributes on #pdp-delivery-estimate:
 *   data-cutoff               – order cutoff time, e.g. "12:00"
 *   data-shipping-days        – transit days (integer)
 *   data-warehouse-offset     – warehouse backlog in business days
 *   data-holidays             – JSON object { name: "YYYY-MM-DD", … }
 *   data-back-in-stock        – ISO date string (product-level restock date)
 *   data-available            – "true" / "false" (initial variant)
 */
(function () {
  'use strict';

  /* ── Helpers ────────────────────────────────────────────── */

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function toDateKey(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function startOfDay(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  function addDays(d, n) {
    var r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
  }

  function diffDays(a, b) {
    return Math.round((startOfDay(b) - startOfDay(a)) / 864e5);
  }

  function parseCutoff(str) {
    /* Returns { hour, minute }. Hour fallback: 12, minute fallback: 0. */
    if (!str) return { hour: 12, minute: 0 };
    var parts = String(str).split(':');
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10);
    return {
      hour:   isNaN(h) ? 12 : h,
      minute: isNaN(m) ? 0  : m
    };
  }

  function cutoffDateOn(day, cfg) {
    /* Build a Date on the given calendar day at the cutoff time. */
    var d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    d.setHours(cfg.cutoffHour, cfg.cutoffMinute, 0, 0);
    return d;
  }

  function parseHolidays(raw) {
    var set = {};
    if (!raw || raw === '[]') return set;
    try {
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      /* Supports both array of date strings ["2026-01-01", …]
         and object { "name": "2026-01-01", … } */
      if (Array.isArray(parsed)) {
        for (var i = 0; i < parsed.length; i++) set[parsed[i]] = true;
      } else {
        for (var key in parsed) {
          if (parsed.hasOwnProperty(key)) set[parsed[key]] = true;
        }
      }
    } catch (e) { /* graceful fallback: no holidays */ }
    return set;
  }

  /* Ship days: Mon–Fri, no holiday */
  function isShipDay(d, holidays) {
    var dow = d.getDay();
    if (dow === 0 || dow === 6) return false;
    return !holidays[toDateKey(d)];
  }

  /* Delivery days: Mon–Sat, no holiday */
  function isDeliveryDay(d, holidays) {
    var dow = d.getDay();
    if (dow === 0) return false; /* Sunday */
    return !holidays[toDateKey(d)];
  }

  function nextShipDay(d, holidays) {
    var r = addDays(d, 1);
    while (!isShipDay(r, holidays)) r = addDays(r, 1);
    return r;
  }

  function nextDeliveryDay(d, holidays) {
    var r = addDays(d, 1);
    while (!isDeliveryDay(r, holidays)) r = addDays(r, 1);
    return r;
  }

  /* ── Core Calculation ──────────────────────────────────── */

  function calculateDelivery(now, cfg) {
    var shippingDays = cfg.shippingDays;
    var offset       = cfg.warehouseOffset;
    var holidays     = cfg.holidays;
    var backInStock  = cfg.backInStock;

    /* Step 1: base ship date */
    var today = startOfDay(now);
    var shipDate;

    if (now < cutoffDateOn(today, cfg) && isShipDay(today, holidays)) {
      shipDate = today;
    } else {
      shipDate = nextShipDay(today, holidays);
    }

    /* Step 2: warehouse offset (advance by N ship days) */
    for (var i = 0; i < offset; i++) {
      shipDate = nextShipDay(shipDate, holidays);
    }

    /* Step 3: back-in-stock constraint */
    if (backInStock) {
      var bis = startOfDay(backInStock);
      if (bis > shipDate) {
        shipDate = isShipDay(bis, holidays) ? bis : nextShipDay(bis, holidays);
      }
    }

    /* Step 4: delivery date = ship date + N delivery days */
    var deliveryDate = shipDate;
    for (var j = 0; j < shippingDays; j++) {
      deliveryDate = nextDeliveryDay(deliveryDate, holidays);
    }

    return { shipDate: shipDate, deliveryDate: deliveryDate };
  }

  /* ── i18n ──────────────────────────────────────────────── */

  /* All user-facing strings for the delivery widget + cart drawer delivery text.
     Active language is derived from <html lang="..."> (set by layout/theme.liquid
     via {{ request.locale.iso_code }}). Fallback is German. */
  var I18N = {
    de: {
      weekdays: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
      months:   ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      in_stock_prefix:        'Auf Lager \u2013 ',
      delivery_label:         'Lieferung: ',
      tomorrow:               'Morgen',
      unavailable:            'Aktuell nicht verfügbar',
      back_in_stock_prefix:   'Wieder verfügbar am ',
      order_within:           'Bestelle innerhalb {time}',
      cart_delivery_tomorrow: 'Voraussichtliche Lieferung: Morgen',
      cart_delivery_weekday:  'Voraussichtliche Lieferung: {weekday}',
      cart_delivery_date:     'Voraussichtliche Lieferung: {date}',
      formatDate: function (d) { return d.getDate() + '. ' + this.months[d.getMonth()]; }
    },
    en: {
      weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      months:   ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'],
      in_stock_prefix:        'In stock \u2013 ',
      delivery_label:         'Delivery: ',
      tomorrow:               'Tomorrow',
      unavailable:            'Currently unavailable',
      back_in_stock_prefix:   'Back in stock on ',
      order_within:           'Order within {time}',
      cart_delivery_tomorrow: 'Estimated delivery: Tomorrow',
      cart_delivery_weekday:  'Estimated delivery: {weekday}',
      cart_delivery_date:     'Estimated delivery: {date}',
      formatDate: function (d) { return this.months[d.getMonth()] + ' ' + d.getDate(); }
    },
    it: {
      weekdays: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'],
      months:   ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
                 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'],
      in_stock_prefix:        'Disponibile \u2013 ',
      delivery_label:         'Consegna: ',
      tomorrow:               'domani',
      unavailable:            'Attualmente non disponibile',
      back_in_stock_prefix:   'Di nuovo disponibile dal ',
      order_within:           'Ordina entro {time}',
      cart_delivery_tomorrow: 'Consegna prevista: domani',
      cart_delivery_weekday:  'Consegna prevista: {weekday}',
      cart_delivery_date:     'Consegna prevista: {date}',
      formatDate: function (d) { return d.getDate() + ' ' + this.months[d.getMonth()]; }
    },
    es: {
      weekdays: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
      months:   ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
                 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
      in_stock_prefix:        'En stock \u2013 ',
      delivery_label:         'Entrega: ',
      tomorrow:               'mañana',
      unavailable:            'Actualmente no disponible',
      back_in_stock_prefix:   'Disponible de nuevo el ',
      order_within:           'Pide dentro de {time}',
      cart_delivery_tomorrow: 'Entrega estimada: mañana',
      cart_delivery_weekday:  'Entrega estimada: {weekday}',
      cart_delivery_date:     'Entrega estimada: {date}',
      formatDate: function (d) { return d.getDate() + ' de ' + this.months[d.getMonth()]; }
    },
    fr: {
      weekdays: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
      months:   ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
      in_stock_prefix:        'En stock \u2013 ',
      delivery_label:         'Livraison\u00a0: ',
      tomorrow:               'demain',
      unavailable:            'Actuellement indisponible',
      back_in_stock_prefix:   'De nouveau disponible le ',
      order_within:           'Commande sous {time}',
      cart_delivery_tomorrow: 'Livraison prévue\u00a0: demain',
      cart_delivery_weekday:  'Livraison prévue\u00a0: {weekday}',
      cart_delivery_date:     'Livraison prévue\u00a0: {date}',
      formatDate: function (d) { return d.getDate() + ' ' + this.months[d.getMonth()]; }
    }
  };

  function currentLang() {
    var raw = (document.documentElement.lang || 'de').toLowerCase();
    var lang = raw.split('-')[0];
    return I18N[lang] ? lang : 'de';
  }

  function locale() { return I18N[currentLang()]; }

  /* Minimal interpolation: t('key', { weekday: 'Mo', date: '5. Mai' }). */
  function t(key, vars) {
    var str = locale()[key] || key;
    if (vars) {
      for (var v in vars) {
        if (vars.hasOwnProperty(v)) {
          str = str.split('{' + v + '}').join(vars[v]);
        }
      }
    }
    return str;
  }

  /* ── Display Formatting ────────────────────────────────── */

  function formatLocalizedDate(d) {
    return locale().formatDate(d);
  }

  function weekdayName(d) {
    return locale().weekdays[d.getDay()];
  }

  /* Backward-compat aliases (historically exported German helpers). */
  function formatGermanDate(d) { return I18N.de.formatDate(d); }
  var WEEKDAYS = I18N.de.weekdays;

  function formatDisplay(result, now) {
    var delivery = result.deliveryDate;
    var days     = diffDays(now, delivery);
    var value;

    if (days <= 1) {
      value = t('tomorrow');
    } else if (days <= 6) {
      value = weekdayName(delivery);
    } else {
      value = formatLocalizedDate(delivery);
    }

    /* Title is rendered as three spans:
         prefix  "Auf Lager – "  (colored green)
         label   "Lieferung: "   (muted)
         value   "Freitag"       (muted but bold — visual accent on the variable part)
       Subtitle is driven by the countdown ticker — see updateCountdown(). */
    return {
      prefix: t('in_stock_prefix'),
      label:  t('delivery_label'),
      value:  value
    };
  }

  /* ── Countdown to next cutoff ──────────────────────────── */

  /* Compute the next cutoff moment as a Date.
     Identical logic to Step 1 of calculateDelivery — shipDate at cutoff time. */
  function nextCutoffAt(now, cfg) {
    var today = startOfDay(now);
    var cutoffToday = cutoffDateOn(today, cfg);
    var shipDate;
    if (now < cutoffToday && isShipDay(today, cfg.holidays)) {
      shipDate = today;
    } else {
      shipDate = nextShipDay(today, cfg.holidays);
    }
    return cutoffDateOn(shipDate, cfg);
  }

  /* Countdown is only shown in the window [20:00 day-before cutoff, cutoff). */
  function countdownWindow(now, cfg) {
    var cutoff = nextCutoffAt(now, cfg);
    var start = new Date(cutoff);
    start.setDate(start.getDate() - 1);
    start.setHours(20, 0, 0, 0);
    return { start: start, end: cutoff };
  }

  /* Format remaining ms as "Xh YYm ZZs" (e.g. "3h 50m 21s" / "0h 05m 07s"). */
  function formatCountdown(ms) {
    if (ms < 0) ms = 0;
    var totalSec = Math.floor(ms / 1000);
    var h = Math.floor(totalSec / 3600);
    var m = Math.floor((totalSec % 3600) / 60);
    var s = totalSec % 60;
    return h + 'h ' + pad(m) + 'm ' + pad(s) + 's';
  }

  /* ── DOM Rendering ─────────────────────────────────────── */

  var el, titleEl, titlePrefixEl, titleLabelEl, titleValueEl, subtitleEl, config;
  var countdownTicker = null;

  function updateCountdown() {
    if (!subtitleEl || !config) return;
    var now = new Date();
    var win = countdownWindow(now, config);
    if (now >= win.start && now < win.end) {
      subtitleEl.textContent = t('order_within', { time: formatCountdown(win.end - now) });
    } else {
      subtitleEl.textContent = '';
    }
  }

  function startCountdown() {
    if (countdownTicker) return;
    updateCountdown();
    countdownTicker = setInterval(updateCountdown, 1000);
  }

  function stopCountdown() {
    if (countdownTicker) {
      clearInterval(countdownTicker);
      countdownTicker = null;
    }
  }

  /* True when a known future restock date is configured (product-level). */
  function isFutureRestock() {
    if (!config || !config.backInStock) return false;
    return startOfDay(config.backInStock) > startOfDay(new Date());
  }

  function render(available) {
    if (!el) return;

    /* Restock state takes precedence over in-stock / unavailable: when a
       product has a known future back-in-stock date, headline it as
       "Wieder verfügbar am {date}" instead of a delivery estimate. */
    if (isFutureRestock()) {
      el.classList.remove('pdp-delivery--in-stock');
      el.classList.remove('pdp-delivery--unavailable');
      el.classList.add('pdp-delivery--restock');
      titlePrefixEl.textContent = t('back_in_stock_prefix');
      titleLabelEl.textContent  = '';
      titleValueEl.textContent  = formatLocalizedDate(startOfDay(config.backInStock));
      subtitleEl.textContent    = '';
      stopCountdown();
      return;
    }

    el.classList.remove('pdp-delivery--restock');

    if (!available) {
      el.classList.remove('pdp-delivery--in-stock');
      el.classList.add('pdp-delivery--unavailable');
      /* Unavailable state: prefix carries the full message, label+value cleared. */
      titlePrefixEl.textContent = t('unavailable');
      titleLabelEl.textContent  = '';
      titleValueEl.textContent  = '';
      subtitleEl.textContent    = '';
      stopCountdown();
      return;
    }

    el.classList.add('pdp-delivery--in-stock');
    el.classList.remove('pdp-delivery--unavailable');

    var result  = calculateDelivery(new Date(), config);
    var display = formatDisplay(result, new Date());

    titlePrefixEl.textContent = display.prefix;  /* "Auf Lager – "   — green */
    titleLabelEl.textContent  = display.label;   /* "Lieferung: "    — muted */
    titleValueEl.textContent  = display.value;   /* "Freitag"        — muted + bold */

    /* Subtitle: dynamic countdown ticker (only renders text within window). */
    startCountdown();
  }

  /* ── Init ───────────────────────────────────────────────── */

  function init() {
    el = document.getElementById('pdp-delivery-estimate');
    if (!el) return;

    titleEl       = document.getElementById('delivery-title');
    titlePrefixEl = titleEl ? titleEl.querySelector('.pdp-delivery-prefix') : null;
    titleLabelEl  = titleEl ? titleEl.querySelector('.pdp-delivery-label')  : null;
    titleValueEl  = titleEl ? titleEl.querySelector('.pdp-delivery-value')  : null;
    subtitleEl    = document.getElementById('delivery-subtitle');

    if (!titlePrefixEl || !titleLabelEl || !titleValueEl) return;

    var bisRaw = el.getAttribute('data-back-in-stock');
    var cutoff = parseCutoff(el.getAttribute('data-cutoff'));

    config = {
      cutoffHour:      cutoff.hour,
      cutoffMinute:    cutoff.minute,
      shippingDays:    parseInt(el.getAttribute('data-shipping-days'), 10) || 1,
      warehouseOffset: parseInt(el.getAttribute('data-warehouse-offset'), 10) || 0,
      holidays:        parseHolidays(el.getAttribute('data-holidays')),
      backInStock:     bisRaw ? new Date(bisRaw) : null
    };

    var available = el.getAttribute('data-available') !== 'false';
    render(available);
  }

  /* Public API – called by product-form.js and cart-drawer.js */
  window.DeliveryEstimate = {
    update: function (available) {
      render(available);
    },
    /* Exported for cart-drawer reuse */
    calculate:           calculateDelivery,
    parseHolidays:       parseHolidays,
    parseCutoff:         parseCutoff,
    diffDays:            diffDays,
    formatCountdown:     formatCountdown,
    nextCutoffAt:        nextCutoffAt,
    /* i18n helpers (current locale) */
    t:                   t,
    currentLang:         currentLang,
    formatDate:          formatLocalizedDate,
    weekdayName:         weekdayName,
    /* Backward-compat aliases (German-only; prefer t() / formatDate() / weekdayName()) */
    formatGermanDate:    formatGermanDate,
    WEEKDAYS:            WEEKDAYS
  };

  /* Boot */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
